/**
 * @author lifei
 * 功能：请求封装
 * update: 2024-9-30 支持请求参数可缺省
 */
import axios, { type AxiosRequestConfig, type Method, type CancelTokenStatic } from 'axios'
import { message } from 'ant-design-vue'
import { isObject, isString, isEqual } from 'lodash-es'
import * as ENUM from '@/enum'
import { toLowerCase, removeChar } from '@/func'
import { Base } from '../base'
import { eventEmitter } from '../eventEmitter'
import { Cache } from '../cache'

type TParams = [string | AxiosRequestConfig, TCommon.Json?, string?]
type TCancel = ReturnType<typeof axios.CancelToken.source>['cancel']
type THeaders = {
  token?: string
  pageId?: string
  busGroupCode?: string
}

export class Service extends Base {
  public constructor() {
    super()
  }

  static {
    Base.register(this)
  }

  public static headers: THeaders = {}

  /**
   * 功能：用于存储接口前缀
   */
  private pathPrefix: string = ''

  /**
   * 功能： 默认请求参数配置
   */
  private defaultOptions: AxiosRequestConfig = {
    timeout: 6000,
    headers: {
      'Content-Type': 'application/json',
    },
  }

  /**
   * 功能：兼容不同中心路径转发问题，默认取 conf 中的配置项
   * 2024-9-23 删除 中心路径配置，通过转发实现
   * @returns
   */
  protected getProxyPath = (): string => this.proxy_api

  /**
   * 功能：get 请求
   * @param params
   * @returns Promise<AxiosResponse<unknown, unknown>>
   */
  protected get = (...params: TParams) => this.handleParam('get', ...params)

  /**
   * 功能： post 请求
   * @param params
   * @returns Promise<AxiosResponse<unknown, unknown>>
   */
  protected post = (...params: TParams) => this.handleParam('post', ...params)

  /**
   * 功能： 上传
   * @param params
   * @returns Promise<AxiosResponse<unknown, unknown>>
   */
  protected upload = (...params: TParams) => {
    let [url, data = {}, showToast = ''] = params
    let options = {}
    if (isString(url)) {
      options = { method: 'get', url, data }
    } else if (isObject(url)) {
      options = url
    } else {
      console.error(`options is wrong, please check your code: ${options}`)
    }
    return this.init({ ...options, method: 'post', headers: { 'Content-Type': 'multipart/form-data' } }, showToast)
  }

  // /**
  //  * 功能： 下载
  //  * @param params
  //  * @returns Promise<AxiosResponse<unknown, unknown>>
  //  */
  /* protected download = (...params: TParams) => {
    let [url, data = {}, showToast = ''] = params
    let options = {}
    if (isString(url)) {
      options = { method: 'get', url, data }
    } else if (isObject(url)) {
      options = url
    } else {
      console.error(`options is wrong, please check your code: ${options}`)
    }
    return this.init({ ...options, method: 'post' }, showToast)
  } */

  /**
   * 功能：处理请求参数
   * @param method 请求方法
   * @param params
   */
  private handleParam = (method: Method, ...params: TParams) => {
    let [url, data = {}, showToast = ''] = params

    let options = {}
    if (isString(url)) {
      options = { method, url, data }
    } else if (isObject(url)) {
      options = url
    } else {
      console.error(`options is wrong, please check your code: ${options}`)
    }
    return this.init(options, showToast)
  }

  /**
   * 功能：处理 axios
   * @param params: 请求参数
   * @param isAbort: on 启动接口取消， off 关闭取消
   * @param showToast: 反馈信息
   */
  private init = (params: AxiosRequestConfig, showToast: string) => {
    Object.assign(params, {
      url: this.formatPath(params.url),
      headers: Object.assign({
        ...this.getHeaders(),
        ...this.defaultOptions.headers,
        ...params.headers,
      }),
      responseType: showToast === 'blob' ? 'blob' : '',
    })

    return axios(params)
      .then((res) => {
        const { data } = res
        if (data.code == '200') {
          showToast && message.success(showToast)
          return Promise.resolve(data.data)
        } else if (showToast === 'blob') {
          return Promise.resolve(data)
        } else {
          this.handleBusStatus(data.code, data.info)
          return Promise.reject(res.data)
        }
      })
      .catch((res) => {
        return Promise.reject(res.data)
      })
  }

  /**
   * 功能：处理业务状态
   * @param code 业务状态码
   */
  private handleBusStatus = (code: keyof typeof ENUM.COMMON.HTTP_BUS_STATUS, msg: string) => {
    const status = ENUM.COMMON.HTTP_BUS_STATUS
    if (code === '500') {
      //处理token异常时的处理
      msg = msg?.indexOf("Unrecognized token 'Load'") !== -1 ? status['500'] : msg
    } else if (code !== '401') {
      msg = msg || status[code]
    } else {
      // 有重定向业务需求，不在前端处理了，服务端通过 nginx 转发。
      msg = '登录过期，请重新登录'
      console.log(code, 'API:401')
      eventEmitter.emit('API:401')
    }
    message.error(msg)
  }

  /**
   * 功能： 设置模块接口路径 path
   * @param pathPrefix: 方法名之外的部分
   */
  protected setPathPrefix = (pathPrefix: string) => {
    this.pathPrefix = pathPrefix
  }

  /**
   * 功能：格式化接口地址
   * @param url 接口 path
   */
  protected formatPath = (url?: string): string => {
    let [proxy_api, pathPrefix, path] = [this.getProxyPath(), this.pathPrefix, url || ''].map((item) => item.replace(/^\/|\/$/g, ''))
    const isOnline = import.meta.env.MODE.includes('online')
    const [version, methodName] = path.split('/')

    if (version && methodName && /^Api/.test(methodName)) {
      const validPath = toLowerCase(removeChar(methodName, 'Api', 'start'), 'capitalize')
      path = `${version}/${validPath}`
    } else {
      throw new Error('The API path is incorrect, example: v1/ApiLogin')
    }
    if (!isOnline) {
      return [`/${proxy_api}`, pathPrefix, path].filter(Boolean).join('/')
    } else {
      // return [`/${proxy_api}`, pathPrefix, path].filter(Boolean).join('/')
      const url = [`${this.proxy_https}`, pathPrefix, path].filter(Boolean).join('/')
      return url
    }
    // return [`${this.proxy_https}`, pathPrefix, path].filter(Boolean).join('/')
  }

  /**
   * 功能：设置自定义请求头
   */
  private getHeaders = () => {
    const { busGroupCode, pageId, token } = Service.headers
    Service.headers.token = token || Cache.LOGIN_INFO.getItem<TUser.Login['ApiDoLogin']['res']>()?.access_token
    return {
      Authorization: `${'Bearer' + ' ' + Service.headers.token}`,
      tenantId: 'XHSD',
      pageId,
      busGroupCode,
    }
  }

  /**
   * 功能：私有化 axios 中的 CancelToken
   */
  private get requestCancelToken(): CancelTokenStatic {
    return axios.CancelToken
  }

  /**
   * 功能：请求任务队列
   * 2024-9-20 新增 header 和 params 判定条件
   */
  private queue = new Map<
    string,
    {
      extParams: {
        data: TCommon.Json<any>
        headers: AxiosRequestConfig['headers']
      }
      timestramp: number
      pending: boolean
      abort: TCancel
    }
  >()

  /**
   * 功能：处理请求任务
   * @param url 接口路径
   */
  private requestAddTask = (url: string, data: Omit<typeof this.queue extends Map<string, infer V> ? V : never, 'timestramp' | 'pending'>) => {
    this.queue.set(url, {
      ...data,
      pending: true,
      timestramp: Date.now(),
    })
  }

  /**
   * 功能：删除任务任务
   * @param url 接口 path
   */
  private requestRemoveTask = (
    url: string = '',
    data: Omit<typeof this.queue extends Map<string, infer V> ? V : never, 'timestramp' | 'pending' | 'abort'>
  ) => {
    const task = this.queue.get(url)
    if (task) {
      const equal = isEqual(task.extParams, data.extParams)
      if (task?.pending && equal) {
        task.pending = false
        this.queue.delete(url)
        task.abort()
      }
    }
  }
}

export const request = new Service()

eventEmitter.on('API:HEADERS', (headers: THeaders) => {
  Service.headers = {
    ...Service.headers,
    ...headers,
  }
})
